/**
 * "Refresh Rillet" — any signed-in viewer can pull fresh ARR, churn and renewal
 * data from Rillet (quick-261104-ia3, John 2026-11-04). Shows when the board's
 * Rillet figures were last pulled; on success the exec panels re-load and every
 * Rillet tile (ARR, churn, CS, Finance, Summary readout) updates.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { apiFetch } from '../../lib/api-client.js';
import { dayLabel } from '../../lib/exec/fmt.js';
import { useRillet } from '../../lib/exec/rillet-source.js';

function friendlyError(err: unknown): string {
  const e = err as { status?: number; code?: string; message?: string };
  if (e.code === 'RILLET_NOT_CONFIGURED' || e.status === 503) return 'Rillet is not connected to the dashboard yet.';
  if (e.status === 502) return 'Rillet did not answer. Try again in a minute.';
  return 'Refresh failed. Try again in a minute.';
}

export function RilletRefreshButton(): React.JSX.Element {
  const R = useRillet();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => apiFetch<{ ok: boolean }>('/api/main/exec/rillet-refresh', { method: 'POST', timeoutMs: 180_000 }),
    onMutate: () => {
      setError(null);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['main', 'exec'] });
    },
    onError: (err) => {
      setError(friendlyError(err));
    },
  });
  return (
    <span className="inline-flex items-center gap-2" data-testid="rillet-refresh">
      <span className="text-[0.68rem] text-ink-400">
        {error ?? `Rillet data pulled ${dayLabel(R.pulledOn)}`}
      </span>
      <button
        type="button"
        onClick={() => {
          m.mutate();
        }}
        disabled={m.isPending}
        title="Pull the latest ARR, churn and renewals from Rillet. Takes about half a minute."
        className="shrink-0 rounded-full border border-ink-200 bg-white px-3 py-1 text-xs font-medium text-brand-600 hover:border-brand-300 disabled:cursor-wait disabled:opacity-60"
      >
        {m.isPending ? 'Refreshing…' : 'Refresh Rillet ↻'}
      </button>
    </span>
  );
}
